/**
 * Sidebar Component
 * Fixed vertical navigation with icon-based items
 * Supports mobile overlay mode
 */
import { memo, useCallback } from 'react';
import { BrandLogo } from '@/components/common/BrandLogo';
import { SidebarNavItem } from './SidebarNavItem';
import { SidePanelToggleButton } from './SidePanelToggleButton';
import type { NavItem, SidebarProps } from './Sidebar.types';
import styles from './Sidebar.module.css';

export const Sidebar = memo(function Sidebar({
  items,
  activePath,
  workspaceShortId,
  isMobileOpen,
  onMobileClose,
  onNavItemClick,
  isCompact,
  showSidePanelToggle,
  className,
}: SidebarProps) {
  const visibleItems = items.filter((item) => !item.hidden);
  const topItems = visibleItems.filter((item) => item.position !== 'bottom');
  const bottomItems = visibleItems.filter((item) => item.position === 'bottom');

  const handleBackdropClick = useCallback(() => {
    onMobileClose?.();
  }, [onMobileClose]);

  const sidebarClassName = [
    styles.sidebar,
    isMobileOpen ? styles.sidebarMobileOpen : '',
    isCompact ? styles.sidebarCompact : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderNavList = (navItems: NavItem[]) => (
    <ul className={styles.navList}>
      {navItems.map((item) => {
        const isActive = item.matchPaths
          ? item.matchPaths.includes(activePath)
          : activePath === item.route;
        const href = `/w/${workspaceShortId}/${item.route}`;

        return (
          <li key={item.id}>
            <SidebarNavItem
              item={item}
              isActive={isActive}
              href={href}
              onNavItemClick={onNavItemClick}
            />
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {isMobileOpen && (
        <div
          className={styles.backdrop}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}
      <nav className={sidebarClassName} aria-label="Main navigation">
        {(isCompact || isMobileOpen) && (
          <div className={styles.logoWrapper}>
            <BrandLogo
              size={isMobileOpen ? 'lg' : 'sm'}
              showLabel
              orientation={isMobileOpen ? 'horizontal' : 'vertical'}
            />
          </div>
        )}
        <div className={styles.navSpacerTop} />
        {renderNavList(topItems)}
        <div className={styles.navSpacer} />
        {bottomItems.length > 0 && renderNavList(bottomItems)}
        {!isMobileOpen && (
          <div
            className={[
              styles.railFooter,
              showSidePanelToggle ? '' : styles.railFooterReserved,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden={!showSidePanelToggle}
          >
            <div className={styles.railSeparator} />
            <SidePanelToggleButton />
          </div>
        )}
      </nav>
    </>
  );
});
